import { Box, Stack, Typography } from '@mui/material';

import type { IntakePracticeVerification } from '../../types';
import { cardSx, chipSx, wf } from '../shell/tokens';

/** What staff verification is: a person reading the intake against each payer's requirements. */
const CHECKS = [
  { label: 'Identifiers', detail: 'Group NPI and TIN agree with NPPES and with each other.' },
  { label: 'Licenses', detail: 'Every license is active in the state you said you bill in.' },
  { label: 'Addresses', detail: 'Service and pay-to addresses are ones the payers will accept.' },
  { label: 'Payer requirements', detail: 'Each payer you picked has the extra forms it demands.' },
];

/** Completion is supplied only from the server journey verification. */
export function IntakeVerificationStatus({
  verified = false,
  practiceVerification,
}: {
  verified?: boolean;
  practiceVerification?: IntakePracticeVerification | null;
}) {
  if (practiceVerification) {
    const counts = practiceVerification.provider_counts;
    const complete = practiceVerification.state === 'verified';
    const awaiting = practiceVerification.state === 'awaiting';
    return (
      <Stack spacing={2} sx={cardSx}>
        <Typography variant="h5" component="h2">
          {complete
            ? 'Your practice’s intake is verified'
            : awaiting
              ? 'JotPsych is verifying your practice’s intake'
              : 'Your practice’s intake is incomplete'}
        </Typography>
        <Typography>
          {counts.verified} of {counts.total} providers verified
        </Typography>
        {!complete && (
          <Typography>
            {counts.awaiting} awaiting review · {counts.incomplete} intake incomplete
          </Typography>
        )}
        <Typography>
          {complete
            ? 'You can now view your practice’s payer connections.'
            : counts.incomplete > 0
              ? 'Complete intake for the remaining providers. Submitted providers can be reviewed while the others finish.'
              : 'Our team is reviewing the submitted intake. This status will update after review.'}
        </Typography>
      </Stack>
    );
  }
  if (verified) {
    return (
      <Stack spacing={2} sx={cardSx}>
        <Typography variant="h5" component="h2">
          Your intake is verified
        </Typography>
        <Typography>
          JotPsych has verified your intake. You can now view your payer connections.
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={2} sx={cardSx}>
      <Box>
        <Box component="span" sx={chipSx('info')}>
          In review
        </Box>
      </Box>
      <Typography variant="h5" component="h2">
        JotPsych is verifying your intake
      </Typography>
      <Typography>
        A person on our enrollment team is reading your intake against what each payer requires.
        We&apos;ll let you know the moment we&apos;re done. After that, the waiting is on your
        payers, not on us.
      </Typography>
      <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {CHECKS.map(check => (
          <Box component="li" key={check.label}>
            <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{check.label}</Typography>
            <Typography sx={{ fontSize: 13.5, color: wf.muted }}>{check.detail}</Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="body2" sx={{ color: wf.muted }}>
        Nothing is needed from you right now. If a check turns up something only you can answer,
        we&apos;ll reach out with that one question instead of sending you back through the form.
      </Typography>
    </Stack>
  );
}
